import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Plus, Search, Trash2, Sparkles, Check, Tags, X, Loader2, Wand2, Filter, CalendarIcon, ArrowUpDown, SlidersHorizontal, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import api from "@/lib/api";

export default function TransactionsPage({ companyId }) {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total_income: 0, total_expenses: 0, net: 0 });
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Basic filters
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterAccount, setFilterAccount] = useState("all");
  
  // Advanced filters
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [filterAiClassified, setFilterAiClassified] = useState("all");
  const [filterNeedsReview, setFilterNeedsReview] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCategory, setBulkCategory] = useState(undefined);
  const [bulkApplying, setBulkApplying] = useState(false);

  // Form state
  const [formAccountId, setFormAccountId] = useState("");
  const [formDate, setFormDate] = useState(new Date().toISOString().split("T")[0]);
  const [formDesc, setFormDesc] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formType, setFormType] = useState("expense");
  const [formCategory, setFormCategory] = useState("uncategorized");
  const [formNotes, setFormNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editCategory, setEditCategory] = useState("");
  
  // AI Classification state
  const [classifying, setClassifying] = useState(false);
  const uncategorizedCount = transactions.filter(t => t.category === "uncategorized").length;

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = {
        search: search || undefined,
        type: filterType !== "all" ? filterType : undefined,
        category: filterCategory !== "all" ? filterCategory : undefined,
        account_id: filterAccount !== "all" ? filterAccount : undefined,
        start_date: dateFrom ? format(dateFrom, "yyyy-MM-dd") : undefined,
        end_date: dateTo ? format(dateTo, "yyyy-MM-dd") : undefined,
        min_amount: minAmount ? parseFloat(minAmount) : undefined,
        max_amount: maxAmount ? parseFloat(maxAmount) : undefined,
        source: filterSource !== "all" ? filterSource : undefined,
        ai_classified: filterAiClassified !== "all" ? filterAiClassified === "true" : undefined,
        needs_review: filterNeedsReview !== "all" ? filterNeedsReview === "true" : undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: 200,
      };
      
      const [txnRes, acctRes, catRes] = await Promise.all([
        api.get(`/companies/${companyId}/transactions`, { params }),
        api.get(`/companies/${companyId}/accounts`),
        api.get("/schedule-c-categories"),
      ]);
      setTransactions(txnRes.data.transactions);
      setTotal(txnRes.data.total);
      setSummary(txnRes.data.summary || { total_income: 0, total_expenses: 0, net: 0 });
      setAccounts(acctRes.data);
      setCategories(catRes.data);
    } catch (err) {
      console.error("Load error:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId, search, filterType, filterCategory, filterAccount, dateFrom, dateTo, minAmount, maxAmount, filterSource, filterAiClassified, filterNeedsReview, sortBy, sortOrder]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formAccountId) { toast.error("Select an account"); return; }
    setSaving(true);
    try {
      await api.post(`/companies/${companyId}/transactions`, {
        account_id: formAccountId,
        date: formDate,
        description: formDesc,
        amount: parseFloat(formAmount),
        type: formType,
        category: formCategory,
        notes: formNotes,
      });
      toast.success("Transaction added");
      setShowAdd(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormAccountId("");
    setFormDate(new Date().toISOString().split("T")[0]);
    setFormDesc("");
    setFormAmount("");
    setFormType("expense");
    setFormCategory("uncategorized");
    setFormNotes("");
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Deleted");
      loadData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleCategoryUpdate = async (txnId, newCat) => {
    try {
      await api.put(`/transactions/${txnId}`, { category: newCat });
      toast.success("Category updated");
      setEditingId(null);
      loadData();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const handleAIClassify = async () => {
    if (uncategorizedCount === 0) {
      toast.info("No uncategorized transactions to classify");
      return;
    }
    setClassifying(true);
    try {
      const res = await api.post(`/companies/${companyId}/transactions/classify`, null, {
        params: { limit: 50 }
      });
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-medium">AI Classification Complete!</span>
          <span className="text-sm opacity-90">{res.data.classified} transactions categorized</span>
        </div>
      );
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Classification failed");
    } finally {
      setClassifying(false);
    }
  };

  const getAccountName = (id) => accounts.find(a => a.account_id === id)?.name || id;

  // Reset all filters
  const resetFilters = () => {
    setSearch("");
    setFilterType("all");
    setFilterCategory("all");
    setFilterAccount("all");
    setDateFrom(null);
    setDateTo(null);
    setMinAmount("");
    setMaxAmount("");
    setFilterSource("all");
    setFilterAiClassified("all");
    setFilterNeedsReview("all");
    setSortBy("date");
    setSortOrder("desc");
  };

  // Count active filters
  const activeFilterCount = [
    search, 
    filterType !== "all", 
    filterCategory !== "all", 
    filterAccount !== "all",
    dateFrom, 
    dateTo, 
    minAmount, 
    maxAmount,
    filterSource !== "all",
    filterAiClassified !== "all",
    filterNeedsReview !== "all"
  ].filter(Boolean).length;

  // Bulk selection helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === transactions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(transactions.map(t => t.transaction_id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkCategory(undefined);
  };

  const handleBulkApply = async () => {
    if (!bulkCategory || selectedIds.size === 0) {
      toast.error("Select transactions and a category");
      return;
    }
    setBulkApplying(true);
    try {
      const res = await api.put("/transactions/bulk-category", {
        transaction_ids: Array.from(selectedIds),
        category: bulkCategory,
      });
      toast.success(`${res.data.modified_count} transactions updated to "${res.data.category_name}"`);
      clearSelection();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkApplying(false);
    }
  };

  const isAllSelected = transactions.length > 0 && selectedIds.size === transactions.length;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="transactions-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Transactions</h1>
          <p className="text-muted-foreground mt-1">{total} transactions</p>
        </div>
        <div className="flex items-center gap-3">
          {uncategorizedCount > 0 && (
            <Button 
              variant="outline" 
              onClick={handleAIClassify}
              disabled={classifying}
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
              data-testid="ai-classify-btn"
            >
              {classifying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4 mr-2" />
              )}
              AI Classify ({uncategorizedCount})
            </Button>
          )}
          <Button onClick={() => setShowAdd(true)} data-testid="add-transaction-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Transaction
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {total > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-border/50 bg-[#141b2d]">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Income</p>
              <p className="text-xl font-mono font-bold text-green-400">${summary.total_income.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border-border/50 bg-[#141b2d]">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Expenses</p>
              <p className="text-xl font-mono font-bold text-red-400">${summary.total_expenses.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border-border/50 bg-[#141b2d]">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Net</p>
              <p className={`text-xl font-mono font-bold ${summary.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {summary.net >= 0 ? '+' : ''}${summary.net.toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
        <CardContent className="p-4 space-y-4">
          {/* Basic Filters Row */}
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search transactions..." value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-secondary border-border text-white"
                  data-testid="transaction-search" />
              </div>
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px] bg-secondary border-border text-white" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all" className="text-white">All Types</SelectItem>
                <SelectItem value="income" className="text-white">Income</SelectItem>
                <SelectItem value="expense" className="text-white">Expense</SelectItem>
                <SelectItem value="transfer" className="text-white">Transfer</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterAccount} onValueChange={setFilterAccount}>
              <SelectTrigger className="w-[160px] bg-secondary border-border text-white" data-testid="filter-account">
                <SelectValue placeholder="Account" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all" className="text-white">All Accounts</SelectItem>
                {accounts.map(a => (
                  <SelectItem key={a.account_id} value={a.account_id} className="text-white">{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px] bg-secondary border-border text-white" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border max-h-60">
                <SelectItem value="all" className="text-white">All Categories</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.code} value={c.code} className="text-white">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`border-border text-white ${showAdvancedFilters || activeFilterCount > 0 ? 'bg-[#e85d24]/20 border-[#e85d24]/50' : ''}`}
              data-testid="advanced-filters-btn"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-2 bg-[#e85d24] text-white text-xs px-1.5">{activeFilterCount}</Badge>
              )}
            </Button>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="text-muted-foreground" data-testid="reset-filters-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Reset
              </Button>
            )}
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="pt-4 border-t border-border/50 space-y-4 animate-fade-in-up">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Date From */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">From Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal bg-secondary border-border text-white" data-testid="date-from-btn">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {dateFrom ? format(dateFrom, "MMM d, yyyy") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                      <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                
                {/* Date To */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">To Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal bg-secondary border-border text-white" data-testid="date-to-btn">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {dateTo ? format(dateTo, "MMM d, yyyy") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                      <Calendar mode="single" selected={dateTo} onSelect={setDateTo} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                
                {/* Min Amount */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Min Amount</Label>
                  <Input 
                    type="number" 
                    placeholder="$0.00" 
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value)}
                    className="bg-secondary border-border text-white"
                    data-testid="min-amount-input"
                  />
                </div>
                
                {/* Max Amount */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Max Amount</Label>
                  <Input 
                    type="number" 
                    placeholder="$0.00" 
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value)}
                    className="bg-secondary border-border text-white"
                    data-testid="max-amount-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {/* Source */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Source</Label>
                  <Select value={filterSource} onValueChange={setFilterSource}>
                    <SelectTrigger className="bg-secondary border-border text-white" data-testid="filter-source">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="all" className="text-white">All Sources</SelectItem>
                      <SelectItem value="manual" className="text-white">Manual</SelectItem>
                      <SelectItem value="csv" className="text-white">CSV Upload</SelectItem>
                      <SelectItem value="pdf" className="text-white">PDF Upload</SelectItem>
                      <SelectItem value="plaid" className="text-white">Plaid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* AI Classified */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">AI Classified</Label>
                  <Select value={filterAiClassified} onValueChange={setFilterAiClassified}>
                    <SelectTrigger className="bg-secondary border-border text-white" data-testid="filter-ai-classified">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="all" className="text-white">All</SelectItem>
                      <SelectItem value="true" className="text-white">Yes</SelectItem>
                      <SelectItem value="false" className="text-white">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Needs Review */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Needs Review</Label>
                  <Select value={filterNeedsReview} onValueChange={setFilterNeedsReview}>
                    <SelectTrigger className="bg-secondary border-border text-white" data-testid="filter-needs-review">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="all" className="text-white">All</SelectItem>
                      <SelectItem value="true" className="text-white">Yes</SelectItem>
                      <SelectItem value="false" className="text-white">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Sort By */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Sort By</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="bg-secondary border-border text-white" data-testid="sort-by">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="date" className="text-white">Date</SelectItem>
                      <SelectItem value="amount" className="text-white">Amount</SelectItem>
                      <SelectItem value="description" className="text-white">Description</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Sort Order */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Order</Label>
                  <Select value={sortOrder} onValueChange={setSortOrder}>
                    <SelectTrigger className="bg-secondary border-border text-white" data-testid="sort-order">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="desc" className="text-white">Newest First</SelectItem>
                      <SelectItem value="asc" className="text-white">Oldest First</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <Card className="border-primary/50 animate-fade-in-up" style={{ background: 'hsl(225 100% 60% / 0.08)' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Tags className="w-4 h-4 text-primary" />
                <span className="text-sm text-white font-medium">
                  {selectedIds.size} transaction{selectedIds.size > 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center gap-2 flex-1">
                <Select value={bulkCategory} onValueChange={(val) => setBulkCategory(val)}>
                  <SelectTrigger className="w-[240px] bg-secondary border-border text-white" data-testid="bulk-category-select">
                    <SelectValue placeholder="Assign category..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-60">
                    {categories.map(c => (
                      <SelectItem key={c.code} value={c.code} className="text-white">{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handleBulkApply} disabled={bulkApplying || !bulkCategory} size="sm" data-testid="bulk-apply-btn">
                  {bulkApplying ? (
                    <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" /> Applying...</>
                  ) : (
                    <><Check className="w-3.5 h-3.5 mr-1.5" /> Apply</>
                  )}
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={clearSelection} className="text-muted-foreground" data-testid="bulk-clear-btn">
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction Table */}
      <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 bg-secondary" />)}
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground" data-testid="no-transactions">
              <p>No transactions found.</p>
              <p className="text-sm mt-1">Add transactions manually or upload a bank statement.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={toggleSelectAll}
                        className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider">Date</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider">Description</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider">Account</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider">Category</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider text-right">Amount</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase tracking-wider w-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((t) => (
                    <TableRow key={t.transaction_id}
                      className={`border-border/30 hover:bg-secondary/50 ${selectedIds.has(t.transaction_id) ? 'bg-primary/5' : ''}`}
                      data-testid={`txn-row-${t.transaction_id}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(t.transaction_id)}
                          onCheckedChange={() => toggleSelect(t.transaction_id)}
                          className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                          data-testid={`select-txn-${t.transaction_id}`}
                        />
                      </TableCell>
                      <TableCell className="text-sm text-white font-mono">{t.date}</TableCell>
                      <TableCell className="text-sm text-white max-w-[300px] truncate">
                        {t.description}
                        {t.po_number && (
                          <Badge variant="outline" className="ml-2 text-xs border-amber-500/40 text-amber-400 font-mono"
                            title="Linked to purchase order" data-testid={`txn-po-badge-${t.transaction_id}`}>
                            {t.po_number}
                          </Badge>
                        )}
                        {t.ai_classified && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="ml-2 text-xs border-primary/30 text-primary">
                                  <Sparkles className="w-3 h-3 mr-0.5" />
                                  {t.ai_confidence ? `${Math.round(t.ai_confidence * 100)}%` : 'AI'}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>AI classified with {Math.round((t.ai_confidence || 0) * 100)}% confidence</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{getAccountName(t.account_id)}</TableCell>
                      <TableCell>
                        {editingId === t.transaction_id ? (
                          <div className="flex items-center gap-1">
                            <Select value={editCategory} onValueChange={setEditCategory}>
                              <SelectTrigger className="h-8 text-xs bg-secondary border-border text-white w-[150px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border max-h-48">
                                {categories.map(c => (
                                  <SelectItem key={c.code} value={c.code} className="text-white text-xs">{c.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button size="icon" variant="ghost" className="h-7 w-7"
                              onClick={() => handleCategoryUpdate(t.transaction_id, editCategory)}>
                              <Check className="w-3.5 h-3.5 text-profit" />
                            </Button>
                          </div>
                        ) : (
                          <Badge variant="outline" className={`text-xs cursor-pointer ${t.category === 'uncategorized' ? 'border-warning/30 text-warning' : 'border-border text-muted-foreground'}`}
                            onClick={() => { setEditingId(t.transaction_id); setEditCategory(t.category); }}
                            data-testid={`category-badge-${t.transaction_id}`}>
                            {t.category_name}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className={`text-sm font-mono font-medium text-right ${t.type === 'income' ? 'text-profit' : 'text-loss'}`}>
                        {t.type === 'income' ? '+' : '-'}${t.amount.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDelete(t.transaction_id)} data-testid={`delete-txn-${t.transaction_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Transaction Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="add-transaction-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">Add Transaction</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-sm">Account *</Label>
                <Select value={formAccountId} onValueChange={setFormAccountId}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="txn-account-select">
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {accounts.map(a => (
                      <SelectItem key={a.account_id} value={a.account_id} className="text-white">{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Date *</Label>
                <Input type="date" value={formDate} onChange={e => setFormDate(e.target.value)}
                  className="mt-1.5 bg-secondary border-border text-white" data-testid="txn-date-input" />
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Description *</Label>
              <Input value={formDesc} onChange={e => setFormDesc(e.target.value)}
                placeholder="e.g., Office supplies from Amazon"
                className="mt-1.5 bg-secondary border-border text-white" required data-testid="txn-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-sm">Amount *</Label>
                <Input type="number" step="0.01" value={formAmount} onChange={e => setFormAmount(e.target.value)}
                  placeholder="0.00" className="mt-1.5 bg-secondary border-border text-white font-mono" required data-testid="txn-amount-input" />
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Type *</Label>
                <Select value={formType} onValueChange={setFormType}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="txn-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="income" className="text-white">Income</SelectItem>
                    <SelectItem value="expense" className="text-white">Expense</SelectItem>
                    <SelectItem value="transfer" className="text-white">Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Category</Label>
              <Select value={formCategory} onValueChange={setFormCategory}>
                <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="txn-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border max-h-60">
                  {categories.map(c => (
                    <SelectItem key={c.code} value={c.code} className="text-white">{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Notes</Label>
              <Input value={formNotes} onChange={e => setFormNotes(e.target.value)}
                placeholder="Optional notes" className="mt-1.5 bg-secondary border-border text-white" data-testid="txn-notes-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setShowAdd(false)} className="border-border text-white">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="txn-submit-btn">
                {saving ? "Saving..." : "Add Transaction"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
